import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  CircularProgress,
  Paper,
  Chip,
  Autocomplete,
  Checkbox,
  ListItemText,
  FormControlLabel,
  FormGroup,
  Stack,
} from '@mui/material';
import {
  Flag as FlagIcon,
  Language as LanguageIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { useTranslate, useGetList, useAuthProvider } from 'react-admin';

interface BasicDriverInfoStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  onClose: () => void;
  existingData?: any;
  driver?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  userId?: number;
}

export const BasicDriverInfoStep: React.FC<BasicDriverInfoStepProps> = ({
  onNext,
  onBack,
  onClose,
  existingData,
  driver,
  isFirstStep,
  isLastStep,
  userId
}) => {
  const translate = useTranslate();
  const authProvider = useAuthProvider();

  const getInitialFormData = () => {
    const defaultData = {
      userId: userId ? userId.toString() : '',
      profileState: 'in_progress',
      hasCanadaExperience: null,
      canadaProvinces: [],
      canadaExperience: '',
      hasUsaExperience: null,
      usaStates: [],
      usaExperience: '',
      hasInternationalExperience: null,
      internationalExperience: '',
      experienceRadius: ''
    };

    if (driver) {
      // Parse provinces/states immediately if driver exists
      const canadaProvincesList = driver.canadaExperience
        ? driver.canadaExperience.split(',').map(p => p.trim()).filter(p => p && p !== 'null' && p !== 'undefined')
        : [];
      const usaStatesList = driver.usaExperience
        ? driver.usaExperience.split(',').map(s => s.trim()).filter(s => s && s !== 'null' && s !== 'undefined')
        : [];

      const cleanDriver = { ...driver };
      delete cleanDriver.canadaProvinces;
      delete cleanDriver.usaStates;

      return {
        ...defaultData,
        ...cleanDriver,
        userId: driver.userId?.toString() || driver.user?.id?.toString() || defaultData.userId,
        canadaProvinces: canadaProvincesList,
        usaStates: usaStatesList,
        internationalExperience: driver.internationalExperience || '',
        hasCanadaExperience: canadaProvincesList.length > 0 ? true : driver.hasCanadaExperience,
        hasUsaExperience: usaStatesList.length > 0 ? true : driver.hasUsaExperience,
        hasInternationalExperience: driver.internationalExperience ? true : driver.hasInternationalExperience
      };
    }

    return defaultData;
  };

  const [formData, setFormData] = useState(getInitialFormData());

  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Fetch users from API
  const { data: users = [], isLoading: usersLoading } = useGetList('users', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'email', order: 'ASC' },
    filter: {}
  });

  // Check user permissions and get current user
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const identity = await authProvider.getIdentity();
        setCurrentUser(identity);

        // Check if user has admin role
        const permissions = await authProvider.getPermissions();
        setIsAdmin(permissions?.roles?.includes('admin') || false);

        // If no userId provided and not admin, default to current user
        if (!userId && !isAdmin && identity?.id) {
          setFormData(prev => ({ ...prev, userId: identity.id.toString() }));
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
      }
    };

    checkPermissions();
  }, [authProvider, userId]);

  // Update when driver data is loaded
  useEffect(() => {
    if (driver && driver.id) {
      console.log('Driver loaded, updating form data');
      const canadaProvincesList = driver.canadaExperience
        ? driver.canadaExperience.split(',').map(p => p.trim()).filter(p => p && p !== 'null' && p !== 'undefined')
        : [];
      const usaStatesList = driver.usaExperience
        ? driver.usaExperience.split(',').map(s => s.trim()).filter(s => s && s !== 'null' && s !== 'undefined')
        : [];

      console.log('Parsed provinces:', canadaProvincesList);
      console.log('Parsed states:', usaStatesList);

      const cleanDriver = { ...driver };
      delete cleanDriver.canadaProvinces;
      delete cleanDriver.usaStates;

      setFormData(prev => ({
        ...prev,
        ...cleanDriver,
        userId: driver.userId?.toString() || driver.user?.id?.toString() || prev.userId,
        canadaProvinces: canadaProvincesList,
        usaStates: usaStatesList,
        internationalExperience: driver.internationalExperience || '',
        hasCanadaExperience: canadaProvincesList.length > 0 ? true : driver.hasCanadaExperience,
        hasUsaExperience: usaStatesList.length > 0 ? true : driver.hasUsaExperience,
        hasInternationalExperience: driver.internationalExperience ? true : driver.hasInternationalExperience
      }));
    }
  }, [driver]);

  // Update when existingData changes (for wizard state)
  useEffect(() => {
    if (existingData) {
      console.log('Updating from existingData:', existingData);
      setFormData(prev => ({ ...prev, ...existingData }));
    }
  }, [existingData]);

  const experienceRadiusOptions = [
    { value: 'NOT_APPLICABLE', label: 'Not Applicable' },
    { value: 'UP_TO_80KM', label: 'Up to 80km' },
    { value: 'UP_TO_160KM', label: 'Up to 160km' },
    { value: 'UP_TO_500KM', label: 'Up to 500km' },
    { value: 'UP_TO_1000KM', label: 'Up to 1000km' },
    { value: 'MORE_THAN_1000KM', label: 'More than 1000km' },
  ];

  const canadaProvinces = [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
    'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
    'Quebec', 'Saskatchewan', 'Yukon'
  ];



  const usaStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProvinceChange = (province: string, checked: boolean) => {
    // Ensure canadaProvinces is always an array
    const currentProvinces = Array.isArray(formData.canadaProvinces) ? formData.canadaProvinces : [];
    const updatedProvinces = checked
      ? [...currentProvinces, province]
      : currentProvinces.filter(p => p !== province);
    handleInputChange('canadaProvinces', updatedProvinces);
  };

  const handleStateChange = (state: string, checked: boolean) => {
    // Ensure usaStates is always an array
    const currentStates = Array.isArray(formData.usaStates) ? formData.usaStates : [];
    const updatedStates = checked
      ? [...currentStates, state]
      : currentStates.filter(s => s !== state);
    handleInputChange('usaStates', updatedStates);
  };

  const handleSave = () => {
    // Ensure arrays before saving
    const provinces = Array.isArray(formData.canadaProvinces) ? formData.canadaProvinces : [];
    const states = Array.isArray(formData.usaStates) ? formData.usaStates : [];

    // Prepare data for saving - convert arrays to comma-separated strings and ensure userId is an integer
    const saveData = {
      ...formData,
      userId: parseInt(formData.userId),
      canadaExperience: provinces.join(', '),
      usaExperience: states.join(', ')
    };
    onNext(saveData);
  };

  return (
    <Box sx={{ maxWidth: '90vw', mx: 'auto', p: 4, maxHeight: '90vh', overflowY: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 600, mb: 1, color: 'grey.800' }}>
          Driving Experience Profile
        </Typography>
        <Typography variant="body1" sx={{ color: 'grey.600', fontSize: '1.1rem' }}>
          Tell us about your driving experience to help match you with the right opportunities
        </Typography>
      </Box>

      <Stack spacing={3}>

        {/* Experience Radius */}
        <Box>
          <Paper elevation={0} sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            bgcolor: 'white',
            width: '95%',
            mx: 'auto'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
              What is the longest distance of vehicle operation you have experience with?
            </Typography>

            <Box>
              <FormControl fullWidth>
                <Select
                  value={formData.experienceRadius}
                  onChange={(e) => handleInputChange('experienceRadius', e.target.value)}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      py: 2,
                      fontSize: '1.1rem'
                    }
                  }}
                >
                  <MenuItem value="" sx={{ color: 'grey.500' }}>Choose your experience level...</MenuItem>
                  {experienceRadiusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ py: 1.5 }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>
        </Box>

        {/* Canada Experience */}
        <Box>
          <Paper elevation={0} sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            bgcolor: 'white',
            width: '95%',
            mx: 'auto'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
              Do you have commercial driving experience in Canada?
            </Typography>

            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <Select
                  value={formData.hasCanadaExperience === null ? '' : formData.hasCanadaExperience.toString()}
                  onChange={(e) => handleInputChange('hasCanadaExperience', e.target.value === '' ? null : e.target.value === 'true')}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      py: 2,
                      fontSize: '1.1rem'
                    }
                  }}
                >
                  <MenuItem value="" sx={{ color: 'grey.500' }}>Choose an option...</MenuItem>
                  <MenuItem value="true">Yes, I have Canada experience</MenuItem>
                  <MenuItem value="false">No Canada experience</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {formData.hasCanadaExperience && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, textAlign: 'center' }}>
                  Select the provinces where you have experience
                </Typography>
                <Box sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  justifyContent: 'center'
                }}>
                  {canadaProvinces.map((province) => {
                    const provinces = Array.isArray(formData.canadaProvinces) ? formData.canadaProvinces : [];
                    const isSelected = provinces.includes(province);
                    return (
                    <Chip
                      key={province}
                      label={province}
                      onClick={() => handleProvinceChange(province, !isSelected)}
                      variant={isSelected ? 'filled' : 'outlined'}
                      color={isSelected ? 'success' : 'default'}
                      sx={{
                        py: 0.5,
                        px: 1,
                        fontSize: '0.95rem',
                        '&:hover': {
                          bgcolor: isSelected ? 'success.dark' : 'success.50'
                        },
                        cursor: 'pointer'
                      }}
                    />
                  );
                  })}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>

        {/* USA Experience */}
        <Box>
          <Paper elevation={0} sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            bgcolor: 'white',
            width: '95%',
            mx: 'auto'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
              Do you have commercial driving experience in the United States?
            </Typography>

            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <Select
                  value={formData.hasUsaExperience === null ? '' : formData.hasUsaExperience.toString()}
                  onChange={(e) => handleInputChange('hasUsaExperience', e.target.value === '' ? null : e.target.value === 'true')}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      py: 2,
                      fontSize: '1.1rem'
                    }
                  }}
                >
                  <MenuItem value="" sx={{ color: 'grey.500' }}>Choose an option...</MenuItem>
                  <MenuItem value="true">Yes, I have USA experience</MenuItem>
                  <MenuItem value="false">No USA experience</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {formData.hasUsaExperience && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, textAlign: 'center' }}>
                  Select the states where you have experience
                </Typography>
                <Box sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  justifyContent: 'center'
                }}>
                  {usaStates.map((state) => (
                    <Chip
                      key={state}
                      label={state}
                      onClick={() => handleStateChange(state, !formData.usaStates.includes(state))}
                      variant={formData.usaStates.includes(state) ? 'filled' : 'outlined'}
                      color={formData.usaStates.includes(state) ? 'info' : 'default'}
                      sx={{
                        py: 0.5,
                        px: 1,
                        fontSize: '0.95rem',
                        '&:hover': {
                          bgcolor: formData.usaStates.includes(state) ? 'info.dark' : 'info.50'
                        },
                        cursor: 'pointer'
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>

        {/* International Experience */}
        <Box>
          <Paper elevation={0} sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            bgcolor: 'white',
            width: '95%',
            mx: 'auto'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
              Do you have commercial driving experience outside Canada and the USA?
            </Typography>

            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <Select
                  value={formData.hasInternationalExperience === null ? '' : formData.hasInternationalExperience.toString()}
                  onChange={(e) => handleInputChange('hasInternationalExperience', e.target.value === '' ? null : e.target.value === 'true')}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      py: 2,
                      fontSize: '1.1rem'
                    }
                  }}
                >
                  <MenuItem value="" sx={{ color: 'grey.500' }}>Choose an option...</MenuItem>
                  <MenuItem value="true">Yes, I have international experience</MenuItem>
                  <MenuItem value="false">No international experience</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {formData.hasInternationalExperience && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, textAlign: 'center' }}>
                  Describe your international driving experience
                </Typography>
                <MuiTextField
                  fullWidth
                  multiline
                  rows={5}
                  value={formData.internationalExperience}
                  onChange={(e) => handleInputChange('internationalExperience', e.target.value)}
                  placeholder="Tell us about the countries you've driven in, types of routes, duration of experience, cargo types, distance covered, etc."
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'grey.50'
                    }
                  }}
                />
              </Box>
            )}
          </Paper>
        </Box>
      </Stack>

      {/* Action Buttons */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mt: 4,
        pt: 4,
        borderTop: '1px solid',
        borderColor: 'grey.200'
      }}>
        <Button
          variant="outlined"
          size="large"
          onClick={onClose}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 1,
            borderColor: 'grey.300',
            color: 'grey.700',
            fontSize: '1rem',
            textTransform: 'none',
            '&:hover': {
              borderColor: 'grey.400',
              bgcolor: 'grey.50'
            }
          }}
        >
          Close
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={!formData.experienceRadius}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 1,
            fontSize: '1rem',
            textTransform: 'none',
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark'
            },
            '&:disabled': {
              backgroundColor: 'grey.300'
            }
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};